import { useState, useEffect, useContext } from "react";
import "./common.scss";
import FormRendererContext from "../../services/context/FormRendererContext";
import { currentFormattedDateAndTime, trackEvent } from "../../utils/mixPanel";
import { isUrl } from "../../utils/generalUtils";

// Import all your existing components
import TextField from "./TextField";
import TextArea from "./TextArea";
import NumberInput from "./Number";
import HeightField from "./Height";
import AmericanPhoneField from "./AmericanPhoneField";
import Password from "./Password";
import DateInput from "./DateInput";
import Email from "./Email";
import SingleCheckBox from "./SingleCheckBox";
import DropDownSelector from "./DropDownSelector";
import Telephone from "./Telephone";
import DropDownWithSearch from "./DropDownWithSearch";
import RadioSelector from "./RadioSelector";
import ContentRenderer from "./contentRenderer";
import UploadFile from "./UploadFile";
import CheckBoxSelector from "./CheckBoxSelector";
import DynamicContentRenderer from "./DynamicContentRenderer";
import HealthHistoryAllergyComponent from "./HealthHistoryAllergyComponent";
import HealthHistoryMedicationComponent from "./HealthHistoryMedicationComponent";

const DataGrid = ({ 
  data, 
  setAnswer, 
  formValidation = true, 
  formClass,
  allDepartmentsForSpecificForm = [],
  allReasonsForSpecificForm = [],
}) => {
  const [rows, setRows] = useState(
    data.answer?.value || data.defaultValue || [{}]
  );
  const [validForm, setFormValidation] = useState(formValidation);
  const { answerData } = useContext(FormRendererContext);

  const handleFocus = () => {
    trackEvent(`data-grid ${data?.label} clicked`, {
      question: data?.label,
      questionKey: data?.key,
      clickedAt: currentFormattedDateAndTime(),
    });
  };

  const handleBlur = () => {
    trackEvent(`data-grid ${data?.label} filled`, {
      question: data?.label,
      questionKey: data?.key,
      clickedAt: currentFormattedDateAndTime(),
      data: "Answered",
    });
  };

  const addRow = () => {
    const newRows = [...rows, {}];
    setRows(newRows);
    setAnswer(data, { value: newRows });
    setFormValidation(true);
  };

  const removeRow = (index) => {
    if (rows.length > 1) {
      const newRows = rows.filter((_, i) => i !== index);
      setRows(newRows);
      setAnswer(data, { value: newRows });
    }
  };

  const updateRowData = (rowIndex, componentKey, value) => {
    const newRows = [...rows];
    if (!newRows[rowIndex]) {
      newRows[rowIndex] = {};
    }
    newRows[rowIndex][componentKey] = value.value;
    setRows(newRows);
    // Pass metadata about which specific field was edited
    setAnswer(data, { 
      value: newRows,
      editedFieldKey: componentKey,
      editedRowIndex: rowIndex
    });
    setFormValidation(true);
  };

  const getComponentByType = (item) => {
    let FormComponent = null;
    let specialProps = {};

    switch (item?.type) {
      case "textfield":
        FormComponent = TextField;
        break;
      case "heightField":
        FormComponent = HeightField;
        break;
      case "americanPhoneField":
        FormComponent = AmericanPhoneField;
        break;
      case "textarea":
        FormComponent = TextArea;
        break;
      case "password":
        FormComponent = Password;
        break;
      case "datetime":
        FormComponent = DateInput;
        break;
      case "dob":
        FormComponent = DateInput;
        break;
      case "email":
        FormComponent = Email;
        break;
      case "checkbox":
        FormComponent = SingleCheckBox;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "select":
        FormComponent = DropDownSelector;
        // Special case for department selector
        if (item?.key === "patientDepartmentForSchedulerForSpecificForm") {
          specialProps = {
            data: {
              ...item,
              data: { values: allDepartmentsForSpecificForm },
            },
          };
        }
        break;
      case "number":
        FormComponent = NumberInput;
        break;
      case "phoneNumber":
        FormComponent = Telephone;
        break;
      case "dropDownWithSearch":
        FormComponent = DropDownWithSearch;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "radio":
        FormComponent = RadioSelector;
        // Special case for reason selector
        if (item?.key === "reasonForVisitForScheduler") {
          specialProps = {
            data: {
              ...item,
              values: allReasonsForSpecificForm,
            },
          };
        }
        break;
      case "content":
        FormComponent = ContentRenderer;
        break;
      case "file":
        FormComponent = UploadFile;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "selectboxes":
        FormComponent = CheckBoxSelector;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "dynamicContentRenderer":
        FormComponent = DynamicContentRenderer;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "healthHistoryAllergyComponent":
        FormComponent = HealthHistoryAllergyComponent;
        specialProps = { formValidation: item?.formValidation };
        break;
      case "healthHistoryMedicationComponent":
        FormComponent = HealthHistoryMedicationComponent;
        specialProps = { formValidation: item?.formValidation };
        break;
      default:
        return { FormComponent: null, specialProps: {} };
    }

    return { FormComponent, specialProps };
  };

  const renderComponent = (component, rowIndex, rowData) => {
    const { FormComponent, specialProps } = getComponentByType(component);
    
    if (!FormComponent) {
      return null;
    }

    const componentData = {
      ...component,
      answer: { value: rowData?.[component.key] || "" },
      key: `${component.key}_${rowIndex}`
    };

    // Handle special case for select component data
    const itemData =
      component.type === "select" &&
      component.key === "patientDepartmentForSchedulerForSpecificForm"
        ? {
            ...component,
            data: { values: allDepartmentsForSpecificForm },
          }
        : component.type === "radio" &&
          component.key === "reasonForVisitForScheduler"
        ? {
            ...component,
            values: allReasonsForSpecificForm,
          }
        : componentData;

    const componentSetAnswer = (compData, answerValue) => {
      updateRowData(rowIndex, component.key, answerValue);
    };

    return (
      <FormComponent
        key={`${component.key}_${rowIndex}`}
        data={specialProps.data || itemData}
        setAnswer={componentSetAnswer}
        formValidation={true}
        formClass="mb-0"
        {...specialProps}
      />
    );
  };

  // Group components into rows with max 2 columns
  const groupComponentsIntoRows = (components) => {
    const grouped = [];
    for (let i = 0; i < components.length; i += 2) {
      grouped.push(components.slice(i, i + 2));
    }
    return grouped;
  };

  const componentRows = groupComponentsIntoRows(data.components || []);

  useEffect(() => {
    setFormValidation(formValidation);
    if (data?.answer && data?.answer?.value) {
      setRows(data.answer.value);
      setFormValidation(true);
    } else if (data?.defaultValue) {
      setRows(data.defaultValue);
    }

    if (answerData?.[data?.key]) {
      setRows(answerData[data.key].value);
    }
  }, [data, formValidation]);

  return (
    <div className={`w-full ${formClass}`}>
      <div className="form-title pt-3">
        {data?.label}
        {data?.validate?.required ? (
          <span className={!validForm ? "text-red-700" : "text-gray-600"}>
            {" "}
            *
          </span>
        ) : (
          <></>
        )}
      </div>
      {data?.validate?.required && !validForm ? (
        <span className="text-red-700 font-light text-xs">
          {data?.invalidMessage || "Required Field"}
        </span>
      ) : (
        <></>
      )}

      <div className="data-grid-container mt-2">
        {/* Data Grid Rows */}
        <div className="data-grid-body border rounded">
          {rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className="data-grid-row border-b last:border-b-0 p-4 hover:bg-gray-50 relative"
              onFocus={handleFocus}
              onBlur={handleBlur}
            >
              {/* Row Header with Index and Remove Button */}
              <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
                <div className="text-sm font-medium text-gray-600">
                  Row {rowIndex + 1}
                </div>
                {!data.disableAddingRemovingRows && (
                  <button
                    type="button"
                    onClick={() => removeRow(rowIndex)}
                    disabled={rows.length <= 1}
                    className={`text-red-600 hover:text-red-800 text-sm px-2 py-1 rounded ${
                      rows.length <= 1
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-red-50"
                    }`}
                    title="Remove row"
                  >
                    ✕ Remove
                  </button>
                )}
              </div>

              {/* Component Rows */}
              {componentRows.map((componentRow, compRowIndex) => (
                <div
                  key={compRowIndex}
                  className={`grid gap-4 ${
                    componentRow.length === 1 ? "grid-cols-1" : "grid-cols-2"
                  }`}
                >
                  {componentRow.map((component) => (
                    <div key={component.key} className="w-full">
                      {renderComponent(component, rowIndex, row)}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Add Row Button */}
        {!data.disableAddingRemovingRows && (
          <div className="mt-3">
            <button
              type="button"
              onClick={addRow}
              disabled={data?.disabled}
              className="primary_button primary_button_camelCase"
            //   className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span className="text-lg leading-none">+ </span>
              Add {data?.addAnother || 'Row'}
            </button>
          </div>
        )}
      </div>

      {/* Description */}
      {data?.description && (
        <div className="text-xs text-gray-500 leading-relaxed mt-1">
          {isUrl(data.description) ? (
            <img
              src={data.description}
              alt="Description"
              className="mt-1 rounded"
              style={{ maxHeight: "200px" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
                e.target.parentNode.textContent = data.description;
              }}
            />
          ) : (
            data.description
          )}
        </div>
      )}
    </div>
  );
};

export default DataGrid;